import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useApp, ROLES, ROLE_LABELS } from '../../context/AppContext';
import { cn } from '../../lib/utils';
import {
  LayoutDashboard,
  FolderKanban,
  Upload,
  FileEdit,
  ClipboardCheck,
  Users,
  Building2,
  Package,
  Truck,
  UserCog,
  AlertTriangle,
  Receipt,
  Bell,
  BarChart3,
  Handshake,
  Shield,
  GitBranch,
  ChevronLeft,
  ChevronRight,
  Settings,
  LogOut,
  Archive,
  Wallet,
  IndianRupee,
  ShieldCheck,
  FileCheck,
  MapPin,
  Download,
  ChevronDown,
  Trash2,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

const navItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard', module: 'dashboard' },
  { path: '/cases', icon: FolderKanban, label: 'Case Management', module: 'cases', permission: 'view' },
  {
    path: '/manual-verification',
    icon: ClipboardCheck,
    label: 'Manual Verification',
    module: 'manualVerification',
    subItems: [
      { path: '/manual-verification/pending', label: 'Pending Cases' },
      { path: '/manual-verification/in-progress', label: 'In Progress' },
      { path: '/manual-verification/completed', label: 'Completed Verification' },
      { path: '/manual-verification/rejected', label: 'Rejected Cases' },
      { path: '/manual-verification/drafts', label: 'Draft Saved Cases' }
    ]
  },
  { path: '/analytics', icon: BarChart3, label: 'Analytics', module: 'reports', permission: 'view' },
  { path: '/reports', icon: BarChart3, label: 'Reports & Analytics', module: 'reports', permission: 'view' },
  { path: '/team-performance', icon: Users, label: 'Team Performance', module: 'users', permission: 'view' },
  { path: '/import-export', icon: Upload, label: 'Import / Export', module: 'cases', permission: 'create' },
  { path: '/download-reports', icon: Download, label: 'Download Reports', module: 'reports', permission: 'view' },
  { path: '/removed-cases', icon: Archive, label: 'Removed Cases', module: 'cases', permission: 'delete' },
  { path: '/form-builder', icon: FileEdit, label: 'Form Builder', module: 'formBuilder' },
  { path: '/qc-dashboard', icon: ClipboardCheck, label: 'QC Dashboard', module: 'qualityCheck', permission: 'view' },
  {
    path: '/users',
    icon: Users,
    label: 'User Management',
    module: 'users',
    permission: 'view',
    subItems: [
      { path: '/users/all', label: 'All Users' },
      { path: '/users/create', label: 'Create User' },
      { path: '/users/hierarchy', label: 'Hierarchy Management' },
      { path: '/users/permissions', label: 'User Roles & Permissions' },
      { path: '/users/active', label: 'Active Users' },
      { path: '/users/inactive', label: 'Inactive Users' }
    ]
  },
  { path: '/clients', icon: Building2, label: 'Client Management', module: 'clients', permission: 'view' },
  { path: '/products', icon: Package, label: 'Products & Rates', module: 'products', permission: 'view' },
  { path: '/product-verification', icon: ShieldCheck, label: 'Product & Verification', module: 'products', permission: 'view' },
  { path: '/penalties', icon: AlertTriangle, label: 'Penalty Management', module: 'penalties', permission: 'view' },
  { path: '/invoices', icon: Receipt, label: 'Invoices & Payments', module: 'invoices', permission: 'view' },
  { path: '/payouts', icon: Wallet, label: 'Payout Requests', module: 'payouts', permission: 'view' },
  { path: '/extra-costs', icon: IndianRupee, label: 'Extra Cost Approvals', module: 'cases', permission: 'view' },
  { path: '/notifications', icon: Bell, label: 'Notifications', module: 'notifications', permission: 'view' },
  { path: '/access-control', icon: Shield, label: 'Access Control', module: 'accessControl' },
  { path: '/status-tracker', icon: GitBranch, label: 'Status Tracker', module: 'statusTracker' },
  { path: '/geo-tagger', icon: MapPin, label: 'Manual Geo Tagger', module: 'manualGeo', permission: 'view' },
  { path: '/deletion-requests', icon: Trash2, label: 'Deletion Requests', roles: [ROLES.ADMIN, ROLES.SUPREME_MANAGER] },
  { path: '/vendor', icon: Users, label: 'Vendor Hub', roles: [ROLES.VENDOR] },
  { path: '/vendor-dashboard', icon: LayoutDashboard, label: 'Vendor Dashboard', roles: [ROLES.VENDOR] },
  { path: '/fe-dashboard', icon: LayoutDashboard, label: 'FE Dashboard', roles: [ROLES.FIELD_EXECUTIVE, ROLES.VENDOR_FE] },
  { path: '/bp-dashboard', icon: LayoutDashboard, label: 'BP Dashboard', roles: [ROLES.BUSINESS_PARTNER] },
  { path: '/id-auth', icon: FileCheck, label: 'ID & Authorization', roles: [ROLES.ADMIN] },
];

export const Sidebar = () => {
  const { currentUserId, currentRole, sidebarCollapsed, setSidebarCollapsed, hasPermission } = useApp();
  const location = useLocation();
  const [openSubMenus, setOpenSubMenus] = useState({
    '/manual-verification': true,
    '/users': true,
  });

  const storedUser = (() => {
    try {
      const u = localStorage.getItem('crm_user');
      return u ? JSON.parse(u) : null;
    } catch (e) {
      return null;
    }
  })();
  const activeUser = storedUser || {
    id: currentUserId || 'USR001',
    name: 'Rajesh Kumar',
    email: 'rajesh.kumar@adventures.com',
    role: currentRole || 'admin',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100'
  };

  const toggleSubMenu = (path) => {
    setOpenSubMenus(prev => ({ ...prev, [path]: !prev[path] }));
  };

  const filteredNavItems = navItems.filter(item => {
    // Override: Allow Manual Verification for all specified roles
    if (item.path === '/manual-verification') {
      const allowedRoles = [ROLES.ADMIN, ROLES.SUPREME_MANAGER, ROLES.MANAGER, ROLES.QUALITY_CHECKER, ROLES.VENDOR, ROLES.FIELD_EXECUTIVE];
      return allowedRoles.includes(currentRole);
    }

    // Override: Allow User Management for all specified roles
    if (item.path === '/users') {
      const allowedRoles = [ROLES.ADMIN, ROLES.SUPREME_MANAGER, ROLES.MANAGER, ROLES.VENDOR];
      return allowedRoles.includes(currentRole);
    }

    // Hide standard dashboard for roles that have custom dashboards
    if (item.path === '/') {
      if ([ROLES.BUSINESS_PARTNER, ROLES.FIELD_EXECUTIVE, ROLES.VENDOR_FE, ROLES.UNIVERSAL_QC].includes(currentRole)) {
        return false;
      }
    }

    // Supreme Manager Filter: Allow all operational tabs
    if (currentRole === ROLES.SUPREME_MANAGER) {
      const allowedPaths = [
        '/',                      // Dashboard
        '/cases',                 // Case Management
        '/manual-verification',  // Manual Verification
        '/analytics',             // Analytics
        '/reports',               // Reports & Analytics
        '/team-performance',      // Team Performance
        '/import-export',         // Import / Export (both tabs)
        '/download-reports',      // Download Reports
        '/form-builder',          // Form Builder
        '/qc-dashboard',          // QC Dashboard
        '/users',                 // User Management
        '/clients',               // Client Management
        '/product-verification',  // Document / Product Verification
        '/payouts',               // Payout Requests
        '/extra-costs',           // Extra Cost Approvals
        '/notifications',         // Notifications
        '/status-tracker',        // Status Tracker
        '/geo-tagger',            // Manual Geo Tagger
        '/deletion-requests',     // Deletion Requests
      ];
      if (!allowedPaths.includes(item.path)) return false;
      return true;
    }

    if (item.roles && !item.roles.includes(currentRole)) return false;

    // Supreme Manager and Manager Restrictions
    if (currentRole === ROLES.MANAGER) {
      if (item.path === '/clients') return false;
      if (item.path === '/payouts') return false;
    }

    // Extra costs is only for FE and Supreme Manager (and Admin)
    if (item.path === '/extra-costs') {
      if (![ROLES.ADMIN, ROLES.SUPREME_MANAGER, ROLES.FIELD_EXECUTIVE].includes(currentRole)) return false;
    }

    // QC Restrictions
    if (currentRole === ROLES.QUALITY_CHECKER) {
      if (item.path === '/penalties') return false;
    }

    // Universal QC Restrictions
    if (currentRole === ROLES.UNIVERSAL_QC) {
      if (item.path === '/penalties') return false;
      if (item.path === '/clients') return false;
      if (item.path === '/payouts') return false;
    }

    // FE Restrictions
    if (currentRole === ROLES.FIELD_EXECUTIVE) {
      if (['/cases', '/status-tracker', '/notifications', '/import-export'].includes(item.path)) return false;
    }

    // Vendor FE Restrictions
    if (currentRole === ROLES.VENDOR_FE) {
      if (['/cases', '/penalties', '/status-tracker', '/notifications', '/import-export'].includes(item.path)) return false;
    }

    // Vendor Restrictions: hide Status Tracker and Manual Geo Tagger
    if (currentRole === ROLES.VENDOR) {
      if (['/status-tracker', '/geo-tagger'].includes(item.path)) return false;
    }

    // Client Restrictions
    if (currentRole === ROLES.CLIENT) {
      if (['/analytics', '/reports', '/geo-tagger'].includes(item.path)) return false;
    }

    if (item.roles && item.roles.includes(currentRole)) return true;
    if (item.path === '/vendor') return true;
    if (item.module === 'dashboard') return true;
    if (item.permission) return hasPermission(item.module, item.permission);
    return hasPermission(item.module);
  }).map(item => {
    return item;
  });

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={cn(
          'crm-sidebar flex flex-col',
          sidebarCollapsed && 'collapsed'
        )}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className={cn(
          'flex items-center h-16 px-4 border-b border-slate-200',
          sidebarCollapsed ? 'justify-center' : 'justify-between'
        )}>
          {!sidebarCollapsed && (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
                <span className="text-white font-bold text-sm">AD</span>
              </div>
              <span className="font-semibold text-slate-800 tracking-tight">AD Ventures</span>
            </div>
          )}
          {sidebarCollapsed && (
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
              <span className="text-white font-bold text-sm">AD</span>
            </div>
          )}
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="px-3 space-y-1">
            {filteredNavItems.map((item) => {
              const Icon = item.icon;

              if (item.subItems) {
                const isParentActive = location.pathname.startsWith(item.path);

                if (sidebarCollapsed) {
                  // When collapsed, point the root link to the first subItem
                  const firstSubPath = item.subItems[0].path;
                  const isCollapsedActive = location.pathname.startsWith(item.path);
                  return (
                    <Tooltip key={item.path}>
                      <TooltipTrigger asChild>
                        <NavLink
                          to={firstSubPath}
                          className={cn(
                            'flex items-center justify-center px-3 py-2.5 rounded-lg text-sm font-medium transition-colors relative',
                            isCollapsedActive
                              ? 'bg-blue-50 text-blue-600'
                              : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'
                          )}
                        >
                          {isCollapsedActive && (
                            <div className="absolute left-0 w-0.5 h-5 bg-blue-600 rounded-r" />
                          )}
                          <Icon className="w-5 h-5 flex-shrink-0" />
                        </NavLink>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="bg-white border-slate-200">
                        {item.label}
                      </TooltipContent>
                    </Tooltip>
                  );
                }

                return (
                  <div key={item.path} className="space-y-1">
                    <button
                      onClick={() => toggleSubMenu(item.path)}
                      className={cn(
                        'w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors relative text-slate-500 hover:text-slate-900 hover:bg-slate-100',
                        isParentActive && 'bg-slate-50 text-slate-800'
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="w-5 h-5 flex-shrink-0" />
                        <span>{item.label}</span>
                      </div>
                      <ChevronDown
                        className={cn(
                          'w-4 h-4 transition-transform duration-200 text-slate-400',
                          openSubMenus[item.path] && 'transform rotate-180 text-slate-600'
                        )}
                      />
                    </button>
                    {openSubMenus[item.path] && (
                      <div className="pl-6 space-y-1 border-l border-slate-100 ml-5 animate-in fade-in slide-in-from-top-1 duration-200">
                        {item.subItems.map((subItem) => {
                          const isSubActive = location.pathname === subItem.path;
                          return (
                            <NavLink
                              key={subItem.path}
                              to={subItem.path}
                              className={cn(
                                'flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-colors relative',
                                isSubActive
                                  ? 'bg-blue-50 text-blue-600 font-semibold'
                                  : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'
                              )}
                            >
                              {isSubActive && (
                                <div className="absolute left-0 w-0.5 h-3 bg-blue-600 rounded-r" />
                              )}
                              <span>{subItem.label}</span>
                            </NavLink>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              const isActive = location.pathname === item.path;

              const linkContent = (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors relative',
                    isActive
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'
                  )}
                  data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
                >
                  {isActive && (
                    <div className="absolute left-0 w-0.5 h-5 bg-blue-600 rounded-r" />
                  )}
                  <Icon className={cn('w-5 h-5 flex-shrink-0', sidebarCollapsed && 'mx-auto')} />
                  {!sidebarCollapsed && <span>{item.label}</span>}
                </NavLink>
              );

              if (sidebarCollapsed) {
                return (
                  <Tooltip key={item.path}>
                    <TooltipTrigger asChild>
                      {linkContent}
                    </TooltipTrigger>
                    <TooltipContent side="right" className="bg-white border-slate-200">
                      {item.label}
                    </TooltipContent>
                  </Tooltip>
                );
              }

              return linkContent;
            })}
          </nav>
        </ScrollArea>

        {/* User Section */}
        <div className={cn(
          'border-t border-slate-200 p-4',
          sidebarCollapsed && 'px-2'
        )}>
          {!sidebarCollapsed && (
            <div className="flex items-center gap-3 mb-3">
              <Avatar className="w-9 h-9">
                <AvatarImage src={activeUser.avatar} />
                <AvatarFallback>{activeUser.name?.split(' ').map(n => n[0]).join('') || 'U'}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{activeUser.name}</p>
                <p className="text-xs text-slate-500 truncate">{ROLE_LABELS[currentRole]}</p>
              </div>
            </div>
          )}

          <div className={cn('flex gap-2', sidebarCollapsed && 'flex-col')}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                  data-testid="settings-btn"
                >
                  <Settings className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side={sidebarCollapsed ? 'right' : 'top'}>Settings</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side={sidebarCollapsed ? 'right' : 'top'}>Logout</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                  className="text-slate-500 hover:text-slate-900 hover:bg-slate-100 ml-auto"
                  data-testid="collapse-sidebar-btn"
                >
                  {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent side={sidebarCollapsed ? 'right' : 'top'}>
                {sidebarCollapsed ? 'Expand' : 'Collapse'}
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </aside>
    </TooltipProvider>
  );
};

export default Sidebar;
